import { useEffect, useRef, useState } from 'react';

/** One fetch: the key that names every input it depends on, and how to run it. */
export interface CachedStatsRequest<T> {
    key: string;
    fetch: (signal: AbortSignal) => Promise<T>;
}

/**
 * The caching state machine shared by the stats hooks: one fetch per key, served from a
 * cache the caller owns at module level (so a remount within the session never flashes a
 * loading skeleton), the last figures kept on screen while the next key loads, the entry
 * dropped on error so a retry fetches afresh, and this hook's own abort ignored.
 *
 * A null request means there is nothing to show (no client selected): no data, not loading.
 */
export function useCachedStats<T>(
    request: CachedStatsRequest<T> | null,
    cache: Map<string, T>,
): {
    data: T | null;
    isLoading: boolean;
    isError: boolean;
} {
    const fetchKey = request?.key ?? null;

    // The last value a network call returned (null after an error); shown while the next
    // key loads, so a period or client switch keeps figures on screen instead of a skeleton.
    const [fetchedData, setFetchedData] = useState<T | null>(null);
    // Which key has finished (success or error), derived rather than set at effect start so
    // there is no synchronous setState in the effect body. Initialised from the cache so a
    // remount with a cached key is never "loading".
    const [completedFor, setCompletedFor] = useState<string | null>(() =>
        fetchKey !== null && cache.has(fetchKey) ? fetchKey : null,
    );
    const [isError, setIsError] = useState(false);

    // The latest fetcher, read when the effect runs: callers pass a fresh closure every
    // render, and the key already names everything the fetch depends on, so the closure
    // itself must not re-trigger the effect. Declared before the fetch effect so it is
    // current by the time that effect runs in the same commit.
    const fetchRef = useRef(request?.fetch ?? null);
    useEffect(() => {
        fetchRef.current = request?.fetch ?? null;
    });

    const data = fetchKey !== null ? (cache.get(fetchKey) ?? fetchedData) : null;
    const isLoading = fetchKey !== null && !cache.has(fetchKey) && completedFor !== fetchKey;

    useEffect(() => {
        const fetch = fetchRef.current;
        if (fetchKey === null || fetch === null || cache.has(fetchKey)) return;

        const controller = new AbortController();
        fetch(controller.signal)
            .then((result) => {
                cache.set(fetchKey, result);
                setFetchedData(result);
                setIsError(false);
                setCompletedFor(fetchKey);
            })
            .catch((err: unknown) => {
                if (err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError')) return;
                cache.delete(fetchKey);
                setFetchedData(null);
                setIsError(true);
                setCompletedFor(fetchKey);
            });

        return () => {
            controller.abort();
        };
    }, [fetchKey, cache]);

    return { data, isLoading, isError };
}
